import { Check, Lock, PanelRight, PanelRightOpen, Pencil, ScanEye } from "lucide-react";
import type { ReactNode, RefObject } from "react";
import { Button } from "../../components/ui/Button";
import { CodeTag, TypeChip } from "../../components/ui/misc";
import { MenuButton } from "../../components/ui/PageBar";
import type { DocumentFull } from "../../lib/api/models";
import { formatDateTime } from "../../lib/format";
import { SearchTrigger } from "../search/SearchTrigger";

const KIND: Record<DocumentFull["kind"], string> = { page: "Wiki page", document: "Document", template: "Template" };

/**
 * The document page's header (redesign spec §5.3). It takes the place of the page bar: title, code and status
 * at the left; search, Preview PDF, Export and the primary button at the right.
 */
export function DocumentHeader({
  doc,
  meId,
  title,
  editing,
  onTitleChange,
  status,
  preview,
  onPreview,
  previewButton,
  panelOpen,
  onPanel,
  panelFolded,
  onUnfold,
  unfoldButton,
  canEdit,
  onEdit,
  onDone,
  exportMenu,
  moreMenu,
}: {
  doc: DocumentFull;
  /** The signed-in user: their own lock (another tab, or a reload) is not someone else editing. */
  meId: string | undefined;
  title: string;
  editing: boolean;
  /** Left out while editing without the lock: the title is then read-only. */
  onTitleChange?: (t: string) => void;
  status?: ReactNode;
  /** True while the PDF preview takes the panel's place. */
  preview: boolean;
  onPreview: () => void;
  /** The Preview PDF button, so closing the preview can return focus to it. */
  previewButton: RefObject<HTMLButtonElement | null>;
  /** Below 1280 pixels the panel opens on demand; this is its state there. */
  panelOpen: boolean;
  onPanel: () => void;
  /** From 1280 pixels the panel is open unless folded away; this button brings it back. */
  panelFolded: boolean;
  onUnfold: () => void;
  /** The Unfold panel button, so folding can move focus to it. */
  unfoldButton: RefObject<HTMLButtonElement | null>;
  canEdit: boolean;
  onEdit: () => void;
  onDone: () => void;
  exportMenu: ReactNode;
  /** The menu of rarer actions (Move to…); left out when the user has none. */
  moreMenu?: ReactNode;
}) {
  return (
    <header className="sticky top-0 z-20 border-b border-line bg-column px-4 py-3.5 md:px-7">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
        <MenuButton />
        <div className="min-w-0 flex-1 basis-80">
          <div className="flex flex-wrap items-center gap-2.5">
            {editing ? (
              <input
                aria-label="Title"
                value={title}
                readOnly={!onTitleChange}
                onChange={(e) => onTitleChange?.(e.target.value)}
                className="-ml-1 min-w-0 flex-1 rounded-lg border border-transparent bg-transparent px-1 text-2xl font-semibold text-ink hover:border-line focus:border-green focus:outline-none"
              />
            ) : (
              <h1 className="min-w-0 text-2xl font-semibold text-ink">{title}</h1>
            )}
            {doc.meta.code ? <CodeTag code={doc.meta.code} /> : null}
            <TypeChip code={doc.meta.code} kind={doc.kind} />
            {doc.archivedAt ? (
              <span className="rounded-full bg-danger-wash px-2 py-px text-xs text-danger">Archived</span>
            ) : null}
            {!editing && doc.lock && doc.lock.holder.id !== meId ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-orange-wash px-2 py-px text-xs text-orange-ink">
                <Lock size={12} aria-hidden="true" /> {doc.lock.holder.name} is editing
                {doc.lock.stale ? " (inactive)" : ""}
              </span>
            ) : null}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-ink-3">
            <span>{KIND[doc.kind]}</span>
            {status ??
              (doc.updatedBy ? (
                <span>
                  Edited {formatDateTime(doc.updatedAt)} by {doc.updatedBy.name}
                </span>
              ) : null)}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SearchTrigger variant="icon" />
          <Button
            ref={previewButton}
            aria-pressed={preview}
            onClick={onPreview}
            className={preview ? "border-green! bg-green-wash! text-green-ink!" : undefined}
          >
            <ScanEye size={15} aria-hidden="true" /> Preview PDF
          </Button>
          {exportMenu}
          {moreMenu}
          {preview ? null : (
            <Button
              aria-label={panelOpen ? "Hide panel" : "Show panel"}
              aria-expanded={panelOpen}
              onClick={onPanel}
              className="w-9 px-0! xl:hidden"
            >
              <PanelRight size={16} aria-hidden="true" />
            </Button>
          )}
          {preview || !panelFolded ? null : (
            <Button
              ref={unfoldButton}
              aria-label="Unfold panel"
              onClick={onUnfold}
              className="w-9 px-0! max-xl:hidden!"
            >
              <PanelRightOpen size={16} aria-hidden="true" />
            </Button>
          )}
          {editing ? (
            <Button variant="primary" onClick={onDone}>
              <Check size={15} /> Done
            </Button>
          ) : canEdit ? (
            <Button variant="primary" onClick={onEdit}>
              <Pencil size={15} /> Edit
            </Button>
          ) : null}
        </div>
      </div>
    </header>
  );
}
